/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, memo, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import withErrorStatus from '../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../shared/StatusWrappers/LoadingStatus';
import ConnectionError from '../../../components/shared/ErrorComponents/ConnectionError';
import { isObjectOrArrayAndEmpty } from '../../../components/shared/Utils/Utils';
import {
  ECartAt,
  IDetailComponentProps,
  ISiteCard,
  IUtilityBillElectData,
  IUtilityCostElectData
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import SavingsByMeterDetailComponent, {
  ISavingsByMeterDetailComponentProps
} from './SavingsByMeterDetailComponent';
import { Card } from '@scuf/common';
import { ApiContext } from '../../../context/ApiContext';
import SavingsBySitesDetailComponent, {
  ISavingsBySitesDetailComponentProps
} from '../SavingsBySitesWidget/SavingsBySitesDetailComponent';
type IUtilityData = IUtilityCostElectData | IUtilityBillElectData;
interface ISavingByMeter extends ISiteCard {
  data: IUtilityData;
  UtilityBillType: string;
}

const SavingsByMeterComponentContent = (props: ISavingByMeter) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, UtilityBillType } = props;
  const apiContext = useContext(ApiContext);
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  const savingsCategoryProps = {
    data,
    UtilityBillType
  };
  return (
    <div>
      {apiContext.apiCallAt === ECartAt.portfolioLabel ? (
        <SavingsBySitesDetailComponent
          {...(savingsCategoryProps as ISavingsBySitesDetailComponentProps)}
        />
      ) : (
        <SavingsByMeterDetailComponent
          {...(savingsCategoryProps as ISavingsByMeterDetailComponentProps)}
        />
      )}
    </div>
  );
};

const SavingsByMeterComponentWithLoader = withLoadingStatus(
  withErrorStatus(SavingsByMeterComponentContent)
);

const SavingsByMeterComponent = (props: IDetailComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { dataConnector, UtilityBillType } = props;
  const apiContext = useContext(ApiContext);
  const cartAtLabel = apiContext.apiCallAt;

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const SavingsByMeter = (all as unknown) as IUtilityData;
  const isDataAvailable = SavingsByMeter ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IUtilityData);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(SavingsByMeter);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  const utilitySavingsHeadingList =
    cartAtLabel === ECartAt.siteLabel
      ? t(d.BREAKDOWNOFUTILITY)
      : t(d.UTILITYCOSTBYSITES, {
          count: (data as IUtilityCostElectData)?.breakdownBySite?.length || 0,
          Utility: UtilityBillType
        });

  return (
    <Card className="Savemeter">
      <Card.Header className="emaas-utility-view-details-utilities-savings-meter-card-header">
        {utilitySavingsHeadingList}
      </Card.Header>
      <Card.Content>
        <div className="meter-savings-Datatable">
          <SavingsByMeterComponentWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            UtilityBillType={UtilityBillType!}
          />
        </div>
      </Card.Content>
    </Card>
  );
};
export default memo(SavingsByMeterComponent);
